import { Button } from '@mui/material';
import { keepPreviousData, QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { API_WHITEBOARD, DOMAIN_WHITEBOARD } from '../../../../../modules/API/constants_ts';
import apiManagerWB from '../../../api/apiManagerWB';
import Dialog from '../../../base/ui/components/web/Dialog';
import Input from '../../../base/ui/components/web/Input';
import Select from '../../../base/ui/components/web/Select';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../../../notifications';
import { USER_FUNCTIONS } from '../../../user/constant';
import { checkLimitedFunction } from '../../../user/functions';
import { createNewNameWB } from './function';
import ItemWhiteboard from './ItemWhiteboard';
const useStyles = makeStyles()(theme => {
    return {
        wbDialog: {
            width: 'auto'
        },
        wbContainer: {
            display: 'flex',
            flexDirection: 'column'
        },
        wbSearch: {
            alignSelf: 'end',
            display: 'flex',
            columnGap: 12,
            zIndex: 1
        },
        wbInputSearch: {
            minWidth: '256px'
        },
        wbListContainer: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            columnGap: 20,
            rowGap: 50,
            marginTop: 30,
            marginLeft: 'auto',
            marginRight: 'auto',
            '@media (min-width: 780px)': {
                gridTemplateColumns: '1fr 1fr',
                rowGap: 20,
            },
            '@media (min-width: 1350px)': {
                gridTemplateColumns: '1fr 1fr 1fr',
            }
        },
        wbSelectPermission: {
            display: 'flex',
            flex: 1,
            position: 'absolute',
            bottom: 70,
            '@media (min-width: 540px)': {
                position: 'relative',
                bottom: 'unset'
            },
        }
    };
});
const queryClient = new QueryClient();
export default function DialogManagerWhiteboard() {
    return (React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(ManagerWhiteboard, null)));
}
function ManagerWhiteboard() {
    const { cx, classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const whiteboardPopup = useRef(null);
    const { functions } = useSelector((state) => state['features/user-manage']);
    const { conference } = useSelector((state) => state['features/base/conference']);
    const [currentPermission, setCurrentPermission] = useState(1);
    const [currentPage, setCurrentPage] = useState(5);
    const [currentWhiteboard, setCurrentWhiteboard] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [refresh, setRefresh] = useState(false);
    const { formState: { errors }, control, watch } = useForm();
    const { status, data, error } = useQuery({
        queryKey: [searchValue, refresh, currentPage],
        queryFn: async () => {
            const response = await apiManagerWB.fetchData(`${API_WHITEBOARD}/get/all?page=0&per_page=${currentPage}&wb_name=${searchValue}`);
            return response;
        },
        placeholderData: keepPreviousData,
    });
    // Handle scroll
    const trackLoadCourses = () => {
        if (whiteboardPopup.current) {
            const el = document.querySelector('#form-search-wb');
            if (el)
                el.style.position = whiteboardPopup.current?.scrollTop > 50 ? 'absolute' : 'relative';
        }
        if (whiteboardPopup.current && (whiteboardPopup.current?.offsetHeight + whiteboardPopup.current?.scrollTop === whiteboardPopup.current?.scrollHeight)) {
            setCurrentPage((prevCurrentPage) => {
                return prevCurrentPage + 5;
            });
        }
    };
    const handleChooseWhiteboard = (whiteboard) => {
        setCurrentWhiteboard(whiteboard);
    };
    const handleSubmitDialog = async () => {
        if (currentWhiteboard) {
            // Open whiteboard
            conference?.openWhiteboard(currentWhiteboard.white_board_id, DOMAIN_WHITEBOARD + currentWhiteboard.white_board_id, currentPermission === 1 ? 3 : 2);
        }
        else {
            // Create new whiteboard && open new whiteboard
            try {
                const totalWB = await apiManagerWB.getTotal();
                const canCreate = checkLimitedFunction(functions, USER_FUNCTIONS.MANAGE_WHITEBOARD_CREATE_LIMIT, totalWB?.total_wb);
                if (!canCreate) {
                    return dispatch(showNotification({
                        descriptionKey: "whiteboard.limitedCreateWB",
                        titleKey: "whiteboard.createWB",
                    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                }
                const _data = {
                    name: createNewNameWB(),
                    need_return_data: true,
                };
                const res = await apiManagerWB.apiWB(_data);
                if (res.data.white_board_id) {
                    conference?.openWhiteboard(res.data.white_board_id, DOMAIN_WHITEBOARD + res.data.white_board_id, currentPermission === 1 ? 3 : 2);
                }
            }
            catch (error) {
                console.log(error);
            }
        }
    };
    const maxParticipantsItems = [{
            label: 'whiteboard.onlyView',
            value: 0
        }, {
            label: 'whiteboard.canEdit',
            value: 1
        }].map((no, index) => {
        return {
            value: index + 1,
            label: t(no.label)
        };
    });
    useEffect(() => {
        setTimeout(() => {
            if (whiteboardPopup.current) {
                whiteboardPopup.current.addEventListener("scroll", trackLoadCourses);
            }
        }, 750);
        return () => {
            whiteboardPopup.current?.removeEventListener("scroll", trackLoadCourses);
        };
    }, [whiteboardPopup.current]);
    if (status === 'pending')
        return React.createElement("h1", null, "Loading...");
    if (status === 'error')
        return React.createElement("span", null,
            "Error: ",
            error.message);
    return (React.createElement(Dialog, { titleKey: 'whiteboard.screenTitle', ok: {
            translationKey: 'whiteboard.open',
            disabled: currentWhiteboard === null
        }, className: cx(classes.wbDialog), contentRef: whiteboardPopup, onSubmit: handleSubmitDialog, customElmFooter: (React.createElement("div", { className: cx(classes.wbSelectPermission) },
            React.createElement(Select, { id: 'whiteboard-share-select', options: maxParticipantsItems, onChange: (e) => setCurrentPermission(e.target.value), value: currentPermission }))) },
        React.createElement("div", { className: cx(classes.wbContainer) },
            React.createElement("form", { id: 'form-search-wb', className: cx(classes.wbSearch) },
                React.createElement(Controller, { name: "value", control: control, rules: { required: true, maxLength: 10 }, render: ({ field }) => React.createElement(Input, { className: cx(classes.wbInputSearch), id: 'search-wb', placeholder: t('whiteboard.searchPlaceholder'), clearable: !!(field?.value?.length), error: !!(errors.value), ...field }) }),
                React.createElement(Button, { sx: { whiteSpace: 'nowrap', textWrap: 'nowrap' }, variant: 'contained', size: 'small', onClick: () => {
                        setSearchValue(watch('value'));
                        if (!watch('value')) {
                            setRefresh(prev => !prev);
                        }
                    } }, t('whiteboard.search'))),
            React.createElement("div", { id: 'list-whiteboard', className: cx(classes.wbListContainer) },
                React.createElement(ItemWhiteboard, { isSelected: currentWhiteboard === undefined, isDefault: true, handleChooseWhiteboard: handleChooseWhiteboard }),
                data.data.map((item, index) => (React.createElement(ItemWhiteboard, { key: index, isSelected: currentWhiteboard?.white_board_id === item?.white_board_id, whiteboard: item, handleChooseWhiteboard: handleChooseWhiteboard })))))));
}
